import { usersOrganisationsRolesQueryOptions } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { getOnboardedAppHome } from "@/features/auth/utils/getAuthenticatedPublicLanding";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { NavTabs, NavTabsLink, NavTabsList } from "@patient-watch/ui/tabs";
import { useNav } from "@/utils/nav";
import { Link, Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { ClipboardList, Star, Stethoscope, TrendingDown, UserCheck } from "lucide-react";

export const Route = createFileRoute("/_app/customers")({
    component: CustomersLayout,
    beforeLoad: async ({ context }) => {
        const userId = context?.auth?.id;
        if (!userId || !context?.queryClient) {
            throw redirect({ to: "/login" });
        }

        const roles = await context.queryClient.ensureQueryData(usersOrganisationsRolesQueryOptions(userId));
        context.auth.roles.setRoles(roles ?? []);

        if (!context.auth.roles.isAdmin) {
            throw redirect({ to: getOnboardedAppHome(context.auth.roles) });
        }

        return context;
    },
});

function CustomersLayout() {
    useNav([{ label: "Customers", route: { to: "/customers/success" } }]);

    return (
        <AppPageLayout width="full" padding="lg" as="div" className="flex flex-col gap-4">
            <CustomersNavTabs />
            <Outlet />
        </AppPageLayout>
    );
}

function CustomersNavTabs() {
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    const onChurn = pathname.includes("/customers/churn");
    const onAmbassadors = pathname.includes("/customers/ambassadors");
    const onClinicians = pathname.includes("/customers/clinicians");
    const onAnonymous = pathname.includes("/customers/anonymous");
    const onSuccess = !onChurn && !onAmbassadors && !onClinicians && !onAnonymous;

    return (
        <NavTabs aria-label="Customer views">
            <NavTabsList variant="default">
                <NavTabsLink asChild active={onSuccess}>
                    <Link to="/customers/success">
                        <UserCheck className="w-4 h-4" />
                        Success
                    </Link>
                </NavTabsLink>
                <NavTabsLink asChild active={onChurn}>
                    <Link to="/customers/churn">
                        <TrendingDown className="w-4 h-4" />
                        Churn
                    </Link>
                </NavTabsLink>
                <NavTabsLink asChild active={onAmbassadors}>
                    <Link to="/customers/ambassadors">
                        <Star className="w-4 h-4" />
                        Ambassadors
                    </Link>
                </NavTabsLink>
                <NavTabsLink asChild active={onAnonymous}>
                    <Link to="/customers/anonymous">
                        <ClipboardList className="w-4 h-4" />
                        Anonymous
                    </Link>
                </NavTabsLink>
                <NavTabsLink asChild active={onClinicians}>
                    <Link to="/customers/clinicians">
                        <Stethoscope className="w-4 h-4" />
                        Clinicians
                    </Link>
                </NavTabsLink>
            </NavTabsList>
        </NavTabs>
    );
}
